/**
 * <s-product-thumbnails> — clicking a thumbnail scrolls the product gallery
 * ([data-product-gallery], one large image at a time) to the matching slide.
 * Swiping or scrolling the gallery keeps the active thumbnail (aria-current) in sync.
 */
class SProductThumbnails extends HTMLElement {
  #gallery = null;
  #slides = [];
  #observer = null;

  connectedCallback() {
    const root = this.closest('s-product-image-zoom') ?? document;
    this.#gallery = root.querySelector('[data-product-gallery]');
    if (!this.#gallery) return;
    this.#slides = [...this.#gallery.querySelectorAll('[data-gallery-slide]')];
    this.addEventListener('click', this.#onClick);

    this.#observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) this.#setActive(this.#slides.indexOf(entry.target));
        }
      },
      { root: this.#gallery, threshold: 0.6 },
    );
    this.#slides.forEach((slide) => this.#observer.observe(slide));
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
    this.#observer?.disconnect();
  }

  #onClick = (event) => {
    const thumb = event.target.closest('[data-gallery-thumb]');
    if (!thumb || !this.#gallery) return;
    const index = Number(thumb.dataset.galleryThumb);
    const slide = this.#slides[index];
    if (!slide) return;
    this.#gallery.scrollTo({ left: index * this.#gallery.clientWidth * (document.dir === 'rtl' ? -1 : 1) });
    this.#setActive(index);
  };

  #setActive(index) {
    if (index < 0) return;
    this.querySelectorAll('[data-gallery-thumb]').forEach((thumb) => {
      if (Number(thumb.dataset.galleryThumb) === index) {
        thumb.setAttribute('aria-current', 'true');
        const list = thumb.closest('ul');
        if (list && list.scrollWidth > list.clientWidth) {
          list.scrollTo({ left: thumb.parentElement.offsetLeft - list.clientWidth / 2 + thumb.offsetWidth / 2 });
        }
      } else {
        thumb.removeAttribute('aria-current');
      }
    });
  }
}

if (!customElements.get('s-product-thumbnails')) {
  customElements.define('s-product-thumbnails', SProductThumbnails);
}
